import { formatDate, parseUtcMs } from "@/utils/datetime";
import type { ReactNode } from "react";
import { formatDeclarableRecordingMode } from "@/components/filter/datasetFilters";
import { formatDataOrigin } from "./dataOrigin";
import { formatSchemaVersion } from "./datasetFormatters";
import styles from "./DatasetInfoFacts.module.css";

// 이 파일은 상세 Info 카드의 공통 fact 아이템과 필드 조립·포맷팅을 소유한다.
// 카드 컨테이너/그리드는 각 페이지가 소유한다. 호출자가 리소스에 없는 축의 prop 을 생략하면
// 그 축은 렌더하지 않는다.

export type FactProps = {
  label: string;
  // 대부분 문자열이다. 값 안에 링크가 필요한 호출부(세션 상세 데이터셋 등)만 노드를 전달한다.
  value: string | ReactNode;
  wide?: boolean;
  // 라벨 위/값 아래 대신 "라벨 - 값" 을 한 줄로 표시한다. 목록형 카드가 쓴다.
  horizontal?: boolean;
};

// 단일 라벨-값 아이템. 그리드 컨테이너(<dl className={styles.facts}>)는 호출 측이 감싼다.
export function Fact({ label, value, wide, horizontal }: FactProps): ReactNode {
  return (
    <div
      className={styles.fact}
      data-wide={wide ? "true" : undefined}
      data-layout={horizontal ? "horizontal" : undefined}
    >
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

const EMPTY = "—";

export type DatasetInfoFactsProps = {
  // undefined 필드는 렌더하지 않는다(해당 리소스에 없는 축). null/빈 값은 "—"로 표시한다.
  name?: string;
  operator?: string | null;
  schemaVersion?: string;
  recordingMode?: string | null;
  // 수집 방식이 리뷰어 지정값인지 나타낸다. 생략하면 구조 판정으로 본다.
  // 생략하는 자리의 예: 이 축이 없는 목록형 카드.
  recordingModeModifiedManually?: boolean;
  dataOrigin?: string | null;
  createdAt?: string;
  modifiedAt?: string;
};

// Info 카드 본문(fact 묶음). 호출 측이 <dl className={페이지.facts}> 로 감싼다.
export function DatasetInfoFacts({
  name,
  operator,
  schemaVersion,
  recordingMode,
  recordingModeModifiedManually,
  dataOrigin,
  createdAt,
  modifiedAt,
}: DatasetInfoFactsProps): ReactNode {
  return (
    <>
      {name !== undefined ? <Fact label="이름" value={name} wide /> : null}
      {operator !== undefined ? <Fact label="담당자" value={operator ?? EMPTY} /> : null}
      {schemaVersion !== undefined ? (
        <Fact label="Schema" value={formatSchemaVersion(schemaVersion)} />
      ) : null}
      {recordingMode !== undefined ? (
        <Fact
          label="수집 방식"
          value={formatRecordingModeFact(
            recordingMode,
            dataOrigin,
            recordingModeModifiedManually ?? false,
          )}
        />
      ) : null}
      {dataOrigin !== undefined ? (
        <Fact label="행동 주체" value={formatDataOrigin(dataOrigin) ?? EMPTY} />
      ) : null}
      {createdAt !== undefined ? (
        <Fact label="생성일" value={formatDate(parseUtcMs(createdAt))} />
      ) : null}
      {modifiedAt !== undefined ? (
        <Fact label="수정일" value={formatDate(parseUtcMs(modifiedAt))} />
      ) : null}
    </>
  );
}

// 수집 방식 fact. 리뷰어가 지정한 값에는 "(지정)" 을 붙여 구조 판정과 구분한다.
// 지정된 null 은 "해당 없음" 이다. 지정이 아닌 null 만 빈 표시다.
function formatRecordingModeFact(
  recordingMode: string | null,
  dataOrigin: string | null | undefined,
  declared: boolean,
): string {
  const label = formatDeclarableRecordingMode(recordingMode, dataOrigin, declared);
  if (label === null) {
    return EMPTY;
  }
  return declared ? `${label} (지정)` : label;
}
